import React, { useState, ReactNode, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { Restaurant, SubscriptionStatus } from '../types';
import SubscriptionBlock from './SubscriptionBlock';
import SubscriptionStatusComponent from './SubscriptionStatus';
import './Layout.css';

interface User {
  username?: string;
  userId?: string;
  phone?: string;
  restaurantName?: string;
  restaurantId?: string;
  role?: string;
  subscriptionStatus?: SubscriptionStatus | string | number;
  registrationDate?: string;
  trialDaysRemaining?: number;
}

interface LayoutProps {
  children: ReactNode;
  user: User;
  signOut: () => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: string;
}

const Layout: React.FC<LayoutProps> = ({ children, user, signOut }) => {
  const location = useLocation();
  // Inicializa baseado no tamanho da tela
  const initialIsMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const [isSidebarOpen, setIsSidebarOpen] = useState(!initialIsMobile);
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [showRestaurantSelector, setShowRestaurantSelector] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      const wasMobile = isMobile;
      setIsMobile(mobile);
      
      // Se mudou de mobile para desktop, abrir sidebar
      if (wasMobile && !mobile) {
        setIsSidebarOpen(true);
      }
      // Se mudou de desktop para mobile, fechar sidebar
      else if (!wasMobile && mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  const loadRestaurants = useCallback(async () => {
    try {
      setLoadingRestaurants(true);
      // Employees usam /admin/my-restaurants, admins usam /admin/restaurants
      const endpoint = user.role === 'employee' ? '/admin/my-restaurants' : '/admin/restaurants';
      const response = await api.get<Restaurant[]>(endpoint);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setLoadingRestaurants(false);
    }
  }, [user.role]);

  useEffect(() => {
    loadRestaurants();
  }, [loadRestaurants]);

  const handleRestaurantSelect = (restaurant: Restaurant) => {
    localStorage.setItem('selectedRestaurantId', restaurant.restaurantId);
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      const updatedUser = {
        ...parsedUser,
        restaurantId: restaurant.restaurantId,
        restaurantName: restaurant.name
      };
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    }
    setShowRestaurantSelector(false);
    // Recarregar a página para aplicar as mudanças
    window.location.reload();
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Menu completo para admins
  const allMenuItems: MenuItem[] = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/plans', label: 'Planos de Fidelidade', icon: '🎯' },
    { path: '/transactions', label: 'Transações', icon: '💳' },
    { path: '/clients', label: 'Clientes', icon: '👥' },
    { path: '/gamification', label: 'Gamificação', icon: '🎮' },
    { path: '/employees', label: 'Funcionários', icon: '👷' },
    { path: '/integrations', label: 'Integrações', icon: '🔌' },
    { path: '/profile', label: 'Perfil', icon: '⚙️' },
    { path: '/add-restaurant', label: 'Adicionar Restaurante', icon: '➕' },
  ];

  // Filtrar menu baseado no role
  const userRole = user.role || 'admin'; // Default para admin se não especificado
  const menuItems: MenuItem[] = userRole === 'employee' 
    ? [
        // Employees só veem Transações
        { path: '/transactions', label: 'Transações', icon: '💳' },
      ]
    : allMenuItems;

  return (
    <div className="layout">
      {/* Overlay para mobile */}
      {isMobile && isSidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      <aside className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">✓</span>
            <span className="logo-text">FidHub</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
          
          {restaurants.length > 1 && (
            <button 
              className="nav-item switch-restaurant-btn"
              onClick={() => setShowRestaurantSelector(true)}
            >
              <span className="nav-icon">🏪</span>
              <span className="nav-label">Trocar Loja</span>
            </button>
          )}
        </nav>
        <button className="signout-btn" onClick={signOut}>
          <span className="nav-icon">🚪</span>
          <span className="nav-label">Sair</span>
        </button>
      </aside>

      <div className="main-content">
        <header className="header">
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label="Toggle menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isSidebarOpen ? (
                <>
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </>
              ) : (
                <>
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </>
              )}
            </svg>
          </button>
          <div className="header-user">
            <div className="header-user-info">
              <span className="user-name">{user?.restaurantName || user?.username || user?.phone || 'Usuário'}</span>
              {user?.subscriptionStatus !== undefined && (
                <SubscriptionStatusComponent
                  subscriptionStatus={user.subscriptionStatus}
                  trialDaysRemaining={user.trialDaysRemaining}
                />
              )}
            </div>
            <div className="user-avatar">
              {(user?.restaurantName || user?.username || user?.phone || 'U').charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="content">
          {children}
        </main>
        
        {/* Bloqueio de subscription - sempre renderizar se houver bloqueio no contexto */}
        <SubscriptionBlock
          subscriptionStatus={user?.subscriptionStatus}
          trialDaysRemaining={user?.trialDaysRemaining}
          registrationDate={user?.registrationDate}
        />
      </div>

      {/* Modal de seleção de restaurante */}
      {showRestaurantSelector && (
        <div className="restaurant-selector-modal" onClick={() => setShowRestaurantSelector(false)}>
          <div className="restaurant-selector-content" onClick={(e) => e.stopPropagation()}>
            <div className="restaurant-selector-header">
              <h2>Trocar de Loja</h2>
              <button 
                className="restaurant-selector-close"
                onClick={() => setShowRestaurantSelector(false)}
              >
                ×
              </button>
            </div>
            <div className="restaurant-selector-list">
              {loadingRestaurants ? (
                <div className="restaurant-selector-loading">Carregando...</div>
              ) : (
                restaurants.map((restaurant) => (
                  <button
                    key={restaurant.restaurantId}
                    className={`restaurant-selector-item ${
                      restaurant.restaurantId === user.restaurantId ? 'active' : ''
                    }`}
                    onClick={() => handleRestaurantSelect(restaurant)}
                  >
                    <div className="restaurant-selector-item-name">{restaurant.name}</div>
                    {restaurant.address && (
                      <div className="restaurant-selector-item-address">
                        {restaurant.address.city}, {restaurant.address.state}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;

